/**
 * EntityManager: owns every mob and arrow, runs their 20 Hz tick, natural
 * spawning/despawning, combat (attack / knockback / drops / XP) and the
 * ray-casts the lead needs for the player's melee and the explosion damage.
 *
 * Wiring (see EntityHost in host.ts): build an EntityHost around Player +
 * PlayerStats + World, `new EntityManager(host)`, call `tick()` once per game
 * tick, feed `mobViews()` / `arrowViews()` to the renderer each frame.
 *
 * Mobs only simulate inside Ready chunks and within SIM_RADIUS of the player
 * (else they freeze), and nothing spawns in unloaded chunks.
 */

import type { ArrowView, MobType, MobView } from '../game/types';
import type { TridentView } from './trident';
import { Chicken, Cow, Pig, Sheep } from './animals';
import { Arrow, type ArrowCtx } from './arrow';
import { Creeper, Skeleton, Spider, Zombie } from './hostile';
import { realm } from './realm';
import { effectiveLight, lineClear, NO_PLAYER, segmentAabb, type EntityHost, type PlayerTarget } from './host';
import type { Mob, MobCtx } from './mob';
import { ChunkState } from '../world/chunk';
import { Spawner } from './spawning';
import { difficultyDamage } from '../game/combat';
import { createNetherMob } from './nether/mobs';
import { createWitherMob, witherHooks } from './boss/wither';
import { createWardenMob } from './boss/warden';
import { installShriekers } from './boss/shrieker';
import { wardenHooks } from './boss/wardenHooks';
import { tickBreeding } from './breeding';
import { createVillageMob } from './village/mobs';
import { createRideMob } from './riding/horse';
import { createTraderMob } from './trader/mobs';
import { createMobs3Mob } from './mobs3';
import { mobs3Hooks } from './mobs3/hooks';
import { createOverworld2Mob, overworld2Hooks } from './overworld2/mobs';
import { wolfHooks } from './overworld2/wolf';
import { foxHooks } from './overworld2/fox';
import { AMBIENT_TYPES } from './overworld2/aquatic';
import { navHooks } from './nav/hooks';
import { trampleCheck } from '../world/crops';

export const SIM_RADIUS = 96;
/** Hostile mobs beyond this (3D) from the player are removed outright (vanilla 128). */
export const HOSTILE_DESPAWN = 128;
/** Beyond this, after NO_ACTION_TICKS away from the player, a 1/800 chance per tick (vanilla). */
export const HOSTILE_SOFT_DESPAWN = 32;
export const NO_ACTION_TICKS = 600;

/**
 * Vanilla Mob.checkDespawn for one mob: hostiles go instantly beyond 128
 * blocks, and beyond 32 blocks at 1/800 per tick once they have gone 30 s
 * without the player near. Passive animals never despawn naturally (the
 * spawn caps bound them); `persistent` mobs (name tags, later) never do.
 */
export function despawnCheck(hostile: boolean, persistent: boolean, d2: number, farTicks: number, rnd: () => number): boolean {
  if (!hostile || persistent) return false;
  if (d2 > HOSTILE_DESPAWN * HOSTILE_DESPAWN) return true;
  return d2 > HOSTILE_SOFT_DESPAWN * HOSTILE_SOFT_DESPAWN && farTicks > NO_ACTION_TICKS && rnd() < 1 / 800;
}
const CREEPER_POWER = 3;
const HOSTILE_TYPES = new Set<MobType>(['zombie', 'skeleton', 'creeper', 'spider', 'zombified_piglin', 'piglin', 'magma_cube',
  'ghast', 'blaze', 'wither_skeleton', 'hoglin', 'enderman', 'pillager', 'vindicator', 'zombie_villager',
  'slime', 'cave_spider', 'husk', 'stray', 'drowned', 'silverfish', 'endermite', 'witch', 'phantom', 'wither', 'warden',
  'guardian', 'elder_guardian', 'shulker', 'evoker', 'vex', 'ravager', 'zoglin', 'piglin_brute']);

export const isHostileType = (t: MobType): boolean => HOSTILE_TYPES.has(t);

const EMPTY_TARGETS: readonly PlayerTarget[] = [];
/** Single-player view of `allTargets()` without allocating on every arrow tick. */
const oneCache = new WeakMap<PlayerTarget, readonly PlayerTarget[]>();
function ONE_TARGET(p: PlayerTarget): readonly PlayerTarget[] {
  let list = oneCache.get(p);
  if (!list) { list = [p]; oneCache.set(p, list); }
  return list;
}

export interface EntityManagerOptions {
  /**
   * Dedicated server: there is no local player. `host.player` is never a
   * target; every player is in `remotePlayers`, and despawn / simulation
   * distances are measured to the nearest of them.
   */
  headless?: boolean;
  /** Entity ids from a shared counter (the server numbers mobs, items and players in one space). */
  allocId?: () => number;
  /** Runs each time the module-level hook tables are pointed at this manager (the server adds its own hooks there). */
  onBind?: () => void;
}

/**
 * The manager whose callbacks the module-level hook tables (wolfHooks,
 * wardenHooks, ...) currently point at. A server runs one manager per
 * dimension, so each re-binds the tables before it does anything.
 */
let bound: EntityManager | null = null;
let realmSeq = 0;

/** Stands in for the owner of a pet whose owner is not in this world: right where the pet is, so it idles. */
const ABSENT_OWNER = {
  id: '', x: 0, y: 0, z: 0, eyeY: 0, width: 0.6, height: 1.8, alive: true, creative: true, spectator: false,
  damage: (): void => {}, knockback: (): void => {},
};
const followsOwner = (m: Mob | null): m is Mob => !!m && m.owner !== '' && (m as Mob & { tame?: boolean }).tame === true && 'sitting' in m;

export class EntityManager {
  readonly mobs: Mob[] = [];
  readonly arrows: Arrow[] = [];
  private readonly mobViewList: MobView[] = [];
  private readonly arrowViewList: ArrowView[] = [];
  private nextId = 1;
  private tickCount = 0;
  private readonly spawner: Spawner;
  /**
   * Remote players the host may target, hit and hurt (net/game.ts refreshes
   * this every tick from the session's player states). Empty in single player.
   */
  remotePlayers: readonly PlayerTarget[] = EMPTY_TARGETS;
  /** The mob whose AI is running right now (resolves the target). */
  private current: Mob | null = null;
  private readonly localTarget: PlayerTarget;
  /** `EntityHost` seen by the mobs: its `player` is the nearest target. */
  private readonly mobHost: EntityHost;

  private readonly mobCtx: MobCtx;
  private readonly arrowCtx: ArrowCtx;

  /** Turn natural spawning off (peaceful / tests). */
  spawningEnabled = true;

  private readonly headless: boolean;
  private readonly idSource: (() => number) | undefined;
  private readonly onBind: (() => void) | undefined;
  /** Which world this manager's mobs live in (entities/realm.ts). */
  readonly realmId = ++realmSeq;

  constructor(private readonly host: EntityHost, opts: EntityManagerOptions = {}) {
    const self = this;
    this.headless = opts.headless === true;
    this.idSource = opts.allocId;
    this.onBind = opts.onBind;
    this.localTarget = {
      id: '',
      get x() { return host.player.x; }, get y() { return host.player.y; }, get z() { return host.player.z; },
      get eyeY() { return host.player.eyeY; },
      get width() { return host.player.width; }, get height() { return host.player.height; },
      get alive() { return host.player.alive; },
      get creative() { return host.player.creative; }, get spectator() { return host.player.spectator; },
      damage: (a, src) => host.player.damage(a, src),
      knockback: (dx, dz, s) => host.player.knockback(dx, dz, s),
      setOnFire: (t) => host.player.setOnFire?.(t),
    };
    // Mobs (and the hostile / nether / village AI that reads ctx.host.player)
    // see the nearest player this peer knows about, so one set of AI targets
    // everyone in the room.
    this.mobHost = {
      get world() { return host.world; },
      isNight: () => host.isNight(),
      sunLevel: () => host.sunLevel(),
      get player() { return self.ownerTarget() ?? self.aiTarget() ?? self.fallbackTarget(); },
      explode: (x, y, z, power) => host.explode(x, y, z, power),
      dropItem: (s, x, y, z, vx, vy, vz) => host.dropItem(s, x, y, z, vx, vy, vz),
      dropXp: (a, x, y, z) => host.dropXp(a, x, y, z),
      effect: (k, x, y, z) => host.effect(k, x, y, z),
      eatGrass: (x, y, z) => host.eatGrass?.(x, y, z) ?? false,
      trample: (x, y, z) => host.trample?.(x, y, z),
      difficulty: () => host.difficulty?.() ?? 'normal',
      setBlock: host.setBlock ? (x, y, z, id) => host.setBlock!(x, y, z, id) : undefined,
      crackDoor: (x, y, z, stage) => host.crackDoor?.(x, y, z, stage),
      isRainingAt: host.isRainingAt ? (x, y, z) => host.isRainingAt!(x, y, z) : undefined,
      dayTime: host.dayTime ? () => host.dayTime!() : undefined,
      restTicks: host.restTicks ? () => host.restTicks!() : undefined,
      // a target that carries its own held item / detection scale (server players) answers for itself
      heldItem: () => (self.aiTarget() ?? self.fallbackTarget()).heldItem ?? host.heldItem?.() ?? 0,
      // Invisibility only hides the local player (a remote target's own peer applies its own scale)
      detectScale: () => {
        const t = self.aiTarget() ?? self.fallbackTarget();
        return t.detectScale ?? (t === self.localTarget ? host.detectScale?.() ?? 1 : 1);
      },
      rnd: () => host.rnd(),
    };
    this.mobCtx = {
      host: this.mobHost,
      get world() { return host.world; },
      get tick() { return self.tickCount; },
      los: (a, b, c, d, e, f) => lineClear(host.world, a, b, c, d, e, f),
      targetable: () => self.aiTarget() !== null,
      hurtPlayer: (mob, amount, kb) => {
        const t = self.aiTarget() ?? self.fallbackTarget();
        if (t === NO_PLAYER) return;
        self.lastOwnerHurtBy = mob; self.lastOwnerHurtTick = self.tickCount;
        if (t.id) self.ownerMarks.set(t.id, { mob, tick: self.tickCount });
        amount = difficultyDamage(amount, host.difficulty?.() ?? 'normal');
        if (amount <= 0) return;
        t.damage(amount, { kind: 'melee', x: mob.x, y: mob.y, z: mob.z, attackerId: mob.id });
        t.knockback(t.x - mob.x, t.z - mob.z, kb);
        if (mob.fireTicks > 0) t.setOnFire?.(80);
      },
      shootArrow: (x, y, z, dx, dy, dz, speed, damage, fromPlayer, inaccuracy, shooterId) =>
        this.shootArrow(x, y, z, dx, dy, dz, speed, damage, fromPlayer, inaccuracy, shooterId),
      explodeCreeper: (mob) => this.explodeCreeper(mob),
      shootTrident: (x, y, z, dx, dy, dz, speed, inaccuracy, shooterId) => {
        const a = this.shootArrow(x, y, z, dx, dy, dz, speed, Math.ceil(8 / speed), false, inaccuracy, shooterId);
        a.view.trident = true; a.view.enchanted = false;
      },
    };
    this.arrowCtx = {
      host,
      get world() { return host.world; },
      get mobs() { return self.mobs; },
      /** Every player, local and remote: a skeleton arrow hits whoever is in the way. */
      get players() { return self.allTargets(); },
      attackMob: (m, d, fx, fz, kb, o) => {
        // endermen (and anything with dodgeProjectile) teleport away from arrows instead of being hit
        if ((m as { dodgeProjectile?: () => boolean }).dodgeProjectile?.()) return false;
        // the server's arrows know their shooter's entity id: credit that player, not whoever stands nearest
        const by = o?.shooter ? this.remotePlayers.find((p) => p.eid === o.shooter)?.id : undefined;
        return this.attack(m, d, fx, fz, kb, by ? { fireTicks: o?.fireTicks, by } : o);
      },
      light: (x, y, z) => effectiveLight(host, x, y, z),
    };
    this.bind();
    this.spawner = new Spawner(host, {
      spawnMob: (t, x, y, z) => this.spawnMob(t, x, y, z),
      counts: () => this.counts(),
      anchors: this.headless ? () => this.remotePlayers : undefined,
      countsNear: (x, z, r) => this.counts(x, z, r),
    });
  }

  /** Force the next `bind()` to run again (a hook table was changed from outside). */
  static unbind(): void { bound = null; }

  /** Point the module-level hook tables at this manager (see `bound`). */
  bind(): void {
    if (bound === this) return;
    bound = this;
    realm.id = this.realmId;
    const host = this.host;
    overworld2Hooks.spawn = (t, x, y, z) => this.spawnMob(t, x, y, z);
    mobs3Hooks.mobs = () => this.mobs;
    mobs3Hooks.spawn = (t, x, y, z) => this.spawnMob(t, x, y, z);
    mobs3Hooks.hurtMob = (t, a, from, kb) => this.attackFromMob(t, a, from.x, from.z, kb);
    mobs3Hooks.isHostile = isHostileType;
    // the wither targets every player (remotes too) and every non-undead mob
    witherHooks.players = () => this.allTargets();
    witherHooks.mobs = () => this.mobs;
    // the warden is blind: it hears every player (local and remote) through the vibration bus, not the nearest-target AI
    wardenHooks.players = () => this.allTargets();
    wardenHooks.world = () => host.world;
    wardenHooks.mobs = () => this.mobs;
    wardenHooks.spawn = (t, x, y, z) => this.spawnMob(t, x, y, z);
    wardenHooks.tick = () => this.tickCount;
    wardenHooks.rnd = () => host.rnd();
    wardenHooks.difficulty = () => host.difficulty?.() ?? 'normal';
    installShriekers();
    // tame wolves fight for their owner: the mob the player last hit, else the one that last hurt them
    // creepers flee from cats, skeletons (and strays) from wolves
    navHooks.scaredOf = (me) => {
      const want = me.type === 'creeper' ? 'cat' : me.type === 'skeleton' || me.type === 'stray' ? 'wolf' : '';
      if (!want) return null;
      let best: Mob | null = null, bd = 36;
      for (const m of this.mobs) {
        if (m.type !== want || !m.alive) continue;
        const d = (m.x - me.x) ** 2 + (m.z - me.z) ** 2;
        if (d < bd) { bd = d; best = m; }
      }
      return best;
    };
    wolfHooks.ownerTarget = () => {
      const fresh = (m: Mob | null | undefined, t: number): Mob | null => (m && m.alive && this.tickCount - t < 160 ? m : null);
      // a pet with a known owner fights for that player only
      const owner = this.current?.owner;
      if (owner) {
        const a = this.ownerMarks.get(owner + '>'), b = this.ownerMarks.get(owner);
        return fresh(a?.mob, a?.tick ?? 0) ?? fresh(b?.mob, b?.tick ?? 0);
      }
      return fresh(this.lastPlayerTarget, this.lastPlayerTargetTick) ?? fresh(this.lastOwnerHurtBy, this.lastOwnerHurtTick);
    };
    foxHooks.mobs = () => this.mobs;
    wolfHooks.attack = (attacker, victim, dmg) => {
      if (!victim.alive || !victim.hurt(dmg)) return;
      victim.knockback(victim.x - attacker.x, victim.z - attacker.z, 0.4, this.host.rnd);
      this.host.effect('hurt', victim.x, victim.y + victim.height / 2, victim.z);
      if (victim.dying) this.onDeath(victim);
    };
    this.onBind?.();
  }

  /** The player the mob being ticked belongs to (tamed wolf, cat, parrot), when it has one. */
  private ownerTarget(): PlayerTarget | null {
    const m = this.current;
    if (!followsOwner(m)) return null;
    for (const p of this.remotePlayers) if (p.id === m.owner) return p;
    ABSENT_OWNER.x = m.x; ABSENT_OWNER.y = m.y; ABSENT_OWNER.z = m.z; ABSENT_OWNER.eyeY = m.y + 1.62;
    return ABSENT_OWNER;
  }

  /** The player the mob whose AI is running would act on (hooks that push or poison "the player"). */
  currentTarget(): PlayerTarget { return this.aiTarget() ?? this.fallbackTarget(); }
  /** The mob whose AI is running right now. */
  currentMob(): Mob | null { return this.current; }

  /** What `host.player` is for a mob with nobody to target: the local player, or on a server the nearest player of any kind. */
  private fallbackTarget(): PlayerTarget {
    if (!this.headless) return this.localTarget;
    const m = this.current;
    let best: PlayerTarget = NO_PLAYER, bd = Infinity;
    for (const p of this.remotePlayers) {
      if (p.spectator) continue;
      const d = m ? (p.x - m.x) ** 2 + (p.z - m.z) ** 2 + (p.y - m.y) ** 2 : 0;
      if (d < bd) { bd = d; best = p; }
    }
    return best;
  }

  /** The nearest player a mob may target right now, or null if there is none. */
  private aiTarget(): PlayerTarget | null {
    const m = this.current;
    let best: PlayerTarget | null = null;
    let bestD = Infinity;
    const consider = (p: PlayerTarget): void => {
      if (!p.alive || p.creative || p.spectator) return;
      let d = m ? (p.x - m.x) ** 2 + (p.z - m.z) ** 2 + (p.y - m.y) ** 2 : 0;
      // the player that hurt this mob a moment ago stays its target while within 24 blocks (retaliation)
      if (m && m.lastHurtByPlayer > 0 && m.lastAttacker !== '' && p.id === m.lastAttacker && d < 576) d = -1;
      if (d >= bestD) return;
      bestD = d;
      best = p;
    };
    if (!this.headless) consider(this.localTarget);
    for (const p of this.remotePlayers) consider(p);
    return best;
  }

  /** Every player this peer simulates against: the local one plus remotes. */
  allTargets(): readonly PlayerTarget[] {
    if (this.headless) return this.remotePlayers;
    return this.remotePlayers.length ? [this.localTarget, ...this.remotePlayers] : ONE_TARGET(this.localTarget);
  }

  // ----------------------------------------------------------------- API

  mobViews(): readonly MobView[] { return this.mobViewList; }
  arrowViews(): readonly ArrowView[] { return this.arrowViewList.filter((v) => !v.trident); }
  /** Mob-thrown tridents, for TridentRenderer (ArrowView carries every TridentView field). */
  mobTridentViews(): readonly TridentView[] { return this.arrowViewList.filter((v) => v.trident) as unknown as TridentView[]; }

  stats(): { mobs: number; hostile: number } {
    let hostile = 0;
    for (const m of this.mobs) if (isHostileType(m.type)) hostile++;
    return { mobs: this.mobs.length, hostile };
  }

  /** Mob counts by spawn category; with (x, z, r) only those within r blocks horizontally of the point. */
  private counts(x?: number, z?: number, r = 0): { hostile: number; animals: number; ambient: number } {
    let hostile = 0, ambient = 0, animals = 0;
    for (const m of this.mobs) {
      if (x !== undefined && z !== undefined && (m.x - x) ** 2 + (m.z - z) ** 2 > r * r) continue;
      if (isHostileType(m.type)) { if (!m.dying) hostile++; else if (x === undefined) animals++; }
      else if (AMBIENT_TYPES.has(m.type)) { if (!m.dying) ambient++; else if (x === undefined) animals++; }
      else animals++;
    }
    return { hostile, ambient, animals };
  }

  /** `id` forces the entity id (restoring a replicated mob); normally a fresh one is allocated. */
  spawnMob(type: MobType, x: number, y: number, z: number, id = this.allocId()): Mob | null {
    this.bind();
    const r = this.host.rnd;
    let m: Mob;
    switch (type) {
      case 'zombie': m = new Zombie(id, x, y, z, r); break;
      case 'skeleton': m = new Skeleton(id, x, y, z, r); break;
      case 'creeper': m = new Creeper(id, x, y, z, r); break;
      case 'spider': m = new Spider(id, x, y, z, r); break;
      case 'pig': m = new Pig(id, x, y, z, r); break;
      case 'cow': m = new Cow(id, x, y, z, r); break;
      case 'sheep': m = new Sheep(id, x, y, z, r); break;
      case 'chicken': m = new Chicken(id, x, y, z, r); break;
      default: {
        const n = createNetherMob(type, id, x, y, z, r) ?? createVillageMob(type, id, x, y, z, r)
          ?? createOverworld2Mob(type, id, x, y, z, r) ?? createRideMob(type, id, x, y, z, r)
          ?? createWitherMob(type, id, x, y, z, r) ?? createWardenMob(type, id, x, y, z, r)
          ?? createTraderMob(type, id, x, y, z, r) ?? createMobs3Mob(type, id, x, y, z, r);
        if (!n) return null;
        m = n;
      }
    }
    this.mobs.push(m);
    m.syncView(this.host);
    this.mobViewList.push(m.view);
    return m;
  }

  shootArrow(
    x: number, y: number, z: number, dx: number, dy: number, dz: number,
    speed: number, damage: number, fromPlayer: boolean, inaccuracy = 0, shooterId = 0,
  ): Arrow {
    const len = Math.hypot(dx, dy, dz) || 1;
    const rnd = this.host.rnd;
    // vanilla: gaussian spread of 0.0075 * inaccuracy per component
    const g = (): number => (rnd() + rnd() + rnd() - 1.5) * 0.0075 * inaccuracy * 1.15;
    const nx = dx / len + g(), ny = dy / len + g(), nz = dz / len + g();
    const nl = Math.hypot(nx, ny, nz) || 1;
    const a = new Arrow(this.allocId(), x, y, z, (nx / nl) * speed, (ny / nl) * speed, (nz / nl) * speed,
      damage, fromPlayer, shooterId);
    this.arrows.push(a);
    this.arrowViewList.push(a.view);
    return a;
  }

  /** Fresh entity id (entities/persist.ts). */
  allocId(): number { return this.idSource ? this.idSource() : this.nextId++; }

  /** Take over an arrow built elsewhere (entities/persist.ts restore). */
  adoptArrow(a: Arrow): void {
    this.arrows.push(a);
    this.arrowViewList.push(a.view);
  }

  /** Re-sync a mob's render view after its fields were set from outside. */
  refreshView(m: Mob): void { m.syncView(this.host); }

  /** Closest living mob along a ray (direction need not be normalised). */
  raycast(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number): { mob: Mob; dist: number } | null {
    const l = Math.hypot(dx, dy, dz);
    if (l < 1e-9) return null;
    const ux = dx / l, uy = dy / l, uz = dz / l;
    let best: { mob: Mob; dist: number } | null = null;
    for (const m of this.mobs) {
      if (!m.alive) continue;
      if (Math.abs(m.x - ox) > maxDist + 2 || Math.abs(m.z - oz) > maxDist + 2) continue;
      const hw = m.width / 2;
      const t = segmentAabb(ox, oy, oz, ux * maxDist, uy * maxDist, uz * maxDist,
        [m.x - hw, m.y, m.z - hw, m.x + hw, m.y + m.height, m.z + hw]);
      if (t < 0) continue;
      const dist = t * maxDist;
      if (!best || dist < best.dist) best = { mob: m, dist };
    }
    return best;
  }

  /**
   * Closest other player along a ray, mobs' own ray with a player box. Only
   * `remotePlayers` are candidates (you never swing at yourself), so this is
   * always null in single player.
   */
  raycastPlayer(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number): { player: PlayerTarget; dist: number } | null {
    if (this.remotePlayers.length === 0) return null;
    const l = Math.hypot(dx, dy, dz);
    if (l < 1e-9) return null;
    const ux = dx / l, uy = dy / l, uz = dz / l;
    let best: { player: PlayerTarget; dist: number } | null = null;
    for (const p of this.remotePlayers) {
      if (!p.alive || p.creative || p.spectator) continue;
      if (Math.abs(p.x - ox) > maxDist + 2 || Math.abs(p.z - oz) > maxDist + 2) continue;
      const hw = p.width / 2;
      const t = segmentAabb(ox, oy, oz, ux * maxDist, uy * maxDist, uz * maxDist,
        [p.x - hw, p.y, p.z - hw, p.x + hw, p.y + p.height, p.z + hw]);
      if (t < 0) continue;
      const dist = t * maxDist;
      if (!best || dist < best.dist) best = { player: p, dist };
    }
    return best;
  }

  /**
   * One player's swing at another. The damage travels through `PlayerTarget`
   * (`RemotePlayer` hands it to the peer that owns the body), then knockback
   * points away from the attacker. Vanilla's sweep only ever hits the mobs
   * around the target, so a player hit has none.
   */
  attackPlayer(target: PlayerTarget, damage: number, fromX: number, fromZ: number, knockback = 0.4,
    opts?: { fireTicks?: number }): boolean {
    if (!target.alive || target.creative || target.spectator) return false;
    // fire first: RemotePlayer folds its burning flag into the hit it sends
    if (opts?.fireTicks) target.setOnFire?.(opts.fireTicks);
    if (target.damage(damage, { kind: 'player', x: fromX, y: target.y, z: fromZ }) === 'blocked') return false;
    target.knockback(target.x - fromX, target.z - fromZ, knockback);
    this.host.effect('hurt', target.x, target.y + target.height / 2, target.z);
    return true;
  }

  /**
   * Player (or arrow) hits `mob`: damage, knockback away from (fromX, fromZ)
   * (vanilla 0.4; pass more for sprint hits), hurt flash, and on death the
   * drops + XP. Returns false when the hit was absorbed (invulnerability).
   */
  attack(mob: Mob, damage: number, fromX: number, fromZ: number, knockback = 0.4,
    opts?: { looting?: number; fireTicks?: number; kbLevels?: number; yaw?: number; by?: string }): boolean {
    if (!mob.alive) return false;
    this.bind();
    if (!mob.hurt(damage)) return false;
    const by = opts?.by ?? this.playerAt(fromX, fromZ);
    if (by) this.ownerMarks.set(by + '>', { mob, tick: this.tickCount });
    if (opts?.fireTicks) mob.fireTicks = Math.max(mob.fireTicks, opts.fireTicks);
    this.lootingLevel = opts?.looting ?? 0;
    mob.lastHurtByPlayer = 100;
    mob.lastAttacker = by;
    this.lastPlayerTarget = mob; this.lastPlayerTargetTick = this.tickCount;
    mob.onHurtBy(fromX, fromZ, this.host.rnd);
    mob.knockback(mob.x - fromX, mob.z - fromZ, knockback, this.host.rnd);
    if (opts?.kbLevels) mob.knockbackExtra(opts.kbLevels, opts.yaw ?? 0);
    this.host.effect('hurt', mob.x, mob.y + mob.height / 2, mob.z);
    if (mob.dying) this.onDeath(mob);
    return true;
  }

  /**
   * A mob's projectile or spell hurts `mob` (wither skulls): damage, knockback away from the source, drops
   * on death, but no player credit (no XP, no player-only loot). False when absorbed.
   */
  attackFromMob(mob: Mob, damage: number, fromX: number, fromZ: number, knockback = 0.4): boolean {
    if (!mob.alive || !mob.hurt(damage)) return false;
    mob.onHurtBy(fromX, fromZ, this.host.rnd);
    mob.knockback(mob.x - fromX, mob.z - fromZ, knockback, this.host.rnd);
    this.host.effect('hurt', mob.x, mob.y + mob.height / 2, mob.z);
    if (mob.dying) this.onDeath(mob);
    return true;
  }

  /** Per player id: the mob that last hurt them (key id) and the mob they last hit (key id + '>'). */
  private readonly ownerMarks = new Map<string, { mob: Mob; tick: number }>();

  /** Id of the player standing at (x, z), for hits that do not say who dealt them. */
  private playerAt(x: number, z: number): string {
    for (const p of this.remotePlayers) if (p.id && Math.abs(p.x - x) < 0.01 && Math.abs(p.z - z) < 0.01) return p.id;
    return '';
  }

  private lastPlayerTarget: Mob | null = null;
  private lastPlayerTargetTick = 0;
  private lastOwnerHurtBy: Mob | null = null;
  private lastOwnerHurtTick = 0;
  /** Looting level of the hit currently being processed (read by onDeath). */
  private lootingLevel = 0;

  private onDeath(mob: Mob): void {
    const h = this.host;
    h.effect('death', mob.x, mob.y + mob.height / 2, mob.z);
    const byPlayer = mob.lastHurtByPlayer > 0;
    const cx = mob.x, cy = mob.y + mob.height * 0.5, cz = mob.z;
    const looting = byPlayer ? this.lootingLevel : 0;
    for (const s of mob.loot(h.rnd, byPlayer)) {
      // Looting: +0..level to each counted drop (vanilla enchanted_count_increase)
      if (looting > 0 && s.count > 0 && s.count < 64) s.count += Math.floor(h.rnd() * (looting + 1));
      h.dropItem(s, cx, cy, cz);
    }
    if (byPlayer) {
      const xp = mob.xpValue(h.rnd);
      if (xp > 0) h.dropXp(xp, cx, cy, cz);
    }
  }

  /**
   * Explosion damage + knockback to mobs (and the player when `includePlayer`)
   * within `radius` of the blast, vanilla formula with exposure sampled by
   * line-of-sight. `radius` is normally 2 * power.
   */
  damageInRadius(x: number, y: number, z: number, radius: number, power: number, includePlayer = false): void {
    void power;
    const apply = (ex: number, ey: number, ez: number, h: number): { impact: number; dx: number; dy: number; dz: number } | null => {
      const cx = ex, cy = ey + h / 2, cz = ez;
      const dx = cx - x, dy = cy - y, dz = cz - z;
      const d = Math.hypot(dx, dy, dz);
      if (d > radius) return null;
      const dist = d / radius;
      // exposure: how many of 3 sample points along the body the blast can see
      let seen = 0;
      for (let i = 0; i < 3; i++) {
        const sy = ey + h * (0.1 + 0.4 * i);
        if (lineClear(this.host.world, x, y, z, ex, sy, ez)) seen++;
      }
      const impact = (1 - dist) * (seen / 3);
      if (impact <= 0) return null;
      return { impact, dx, dy, dz };
    };
    for (const m of this.mobs) {
      if (!m.alive) continue;
      if (Math.abs(m.x - x) > radius + 1 || Math.abs(m.z - z) > radius + 1) continue;
      const r = apply(m.x, m.y, m.z, m.height);
      if (!r) continue;
      const dmg = Math.floor((r.impact * r.impact + r.impact) / 2 * 7 * radius + 1);
      m.lastHurtByPlayer = 0;
      if (m.hurt(dmg)) {
        this.host.effect('hurt', m.x, m.y + m.height / 2, m.z);
        if (m.dying) this.onDeath(m);
      }
      m.knockback(r.dx, r.dz, r.impact * 1.2, this.host.rnd);
      m.vy += r.impact * 0.4 * (1 - Math.min(1, m.knockbackResist));
    }
    if (includePlayer) {
      for (const p of this.allTargets()) {
        if (!p.alive || p.spectator || p.creative) continue;
        const r = apply(p.x, p.y, p.z, p.height);
        if (r) {
          const dmg = Math.floor((r.impact * r.impact + r.impact) / 2 * 7 * radius + 1);
          p.damage(dmg, { kind: 'explosion', x, y, z });
          p.knockback(r.dx, r.dz, r.impact * 1.2);
        }
      }
    }
  }

  private explodeCreeper(mob: Mob): void {
    mob.removed = true;
    const x = mob.x, y = mob.y, z = mob.z;
    this.host.explode(x, y, z, CREEPER_POWER);
    this.damageInRadius(x, y + 0.5, z, CREEPER_POWER * 2, CREEPER_POWER, true);
  }

  clear(): void {
    // flag them so side registries (piglin anger, ...) drop their references
    for (const m of this.mobs) m.removed = true;
    this.mobs.length = 0;
    this.arrows.length = 0;
    this.mobViewList.length = 0;
    this.arrowViewList.length = 0;
    this.spawner.reset();
  }

  // ---------------------------------------------------------------- tick

  tick(): void {
    this.tickCount++;
    this.bind();
    const host = this.host;
    const p = host.player;
    const world = host.world;
    const headless = this.headless;
    const anchors = this.remotePlayers;
    if (this.spawningEnabled) this.spawner.tick();

    const mobs = this.mobs;
    for (let i = mobs.length - 1; i >= 0; i--) {
      const m = mobs[i];
      if (m.removed) continue;
      let d2: number, d3: number;
      if (headless) {
        // nearest player of any kind (vanilla: spectators do not hold mobs)
        d2 = d3 = Infinity;
        for (let k = 0; k < anchors.length; k++) {
          const q = anchors[k];
          if (q.spectator) continue;
          const qx = m.x - q.x, qz = m.z - q.z, qy = m.y - q.y;
          const h = qx * qx + qz * qz;
          if (h < d2) d2 = h;
          if (h + qy * qy < d3) d3 = h + qy * qy;
        }
      } else {
        const dx = m.x - p.x, dz = m.z - p.z, dy = m.y - p.y;
        d2 = dx * dx + dz * dz;
        d3 = d2 + dy * dy;
      }
      if (!m.dying && this.shouldDespawn(m, d3)) { m.removed = true; continue; }
      if (d2 > SIM_RADIUS * SIM_RADIUS) continue;
      const ch = world.getChunk(Math.floor(m.x) >> 4, Math.floor(m.z) >> 4);
      if (!ch || ch.state !== ChunkState.Ready) continue; // frozen until its chunk is ready
      const fell = m.onGround ? 0 : m.fallDistance;
      this.current = m;   // the AI target follows the mob being ticked
      m.tick(this.mobCtx);
      // FarmBlock.fallOn: landing on farmland may trample it
      if (fell > 0.5 && m.onGround && trampleCheck(world, m.x, m.y, m.z, fell, m.width, m.height, host.rnd)) host.trample?.(m.x, m.y, m.z);
    }

    // mobs shove each other apart (vanilla 0.05 impulse); only the nearby ones
    for (let i = 0; i < mobs.length; i++) {
      const a = mobs[i];
      if (!a.alive || (!headless && (Math.abs(a.x - p.x) > 48 || Math.abs(a.z - p.z) > 48))) continue;
      for (let j = i + 1; j < mobs.length; j++) {
        const b = mobs[j];
        if (!b.alive) continue;
        const ddx = a.x - b.x;
        if (ddx > 1.4 || ddx < -1.4) continue;
        a.pushFrom(b);
      }
    }

    tickBreeding(mobs, this.breedHooks, this.tickCount);

    for (const a of this.arrows) {
      if (a.removed) continue;
      // frozen in place (stuck or flying) while its chunk is not ready, like mobs
      const ch = world.getChunk(Math.floor(a.x) >> 4, Math.floor(a.z) >> 4);
      if (!ch || ch.state !== ChunkState.Ready) continue;
      a.tick(this.arrowCtx);
    }

    // finish: poof + removal, view lists
    const mv = this.mobViewList;
    mv.length = 0;
    let w = 0;
    for (let i = 0; i < mobs.length; i++) {
      const m = mobs[i];
      if (m.removed) {
        if (m.dying) host.effect('poof', m.x, m.y + m.height / 2, m.z);
        continue;
      }
      m.syncView(host);
      mobs[w++] = m;
      mv.push(m.view);
    }
    mobs.length = w;

    const av = this.arrowViewList;
    av.length = 0;
    const arrows = this.arrows;
    w = 0;
    for (let i = 0; i < arrows.length; i++) {
      if (arrows[i].removed) continue;
      arrows[w++] = arrows[i];
      av.push(arrows[i].view);
    }
    arrows.length = w;
  }

  /**
   * Multiplayer replica tick (net/game.ts): positions and animation are set
   * from the network, so only drop removed entities and rebuild the view
   * lists — no AI, spawning or physics. Mirrored arrows live here too.
   */
  syncViews(): void {
    const mobs = this.mobs;
    const mv = this.mobViewList;
    mv.length = 0;
    let w = 0;
    for (let i = 0; i < mobs.length; i++) {
      const m = mobs[i];
      if (m.removed) continue;
      m.syncView(this.host);
      mobs[w++] = m;
      mv.push(m.view);
    }
    mobs.length = w;

    const arrows = this.arrows;
    const av = this.arrowViewList;
    av.length = 0;
    w = 0;
    for (let i = 0; i < arrows.length; i++) {
      const a = arrows[i];
      if (a.removed) continue;
      arrows[w++] = a;
      av.push(a.view);
    }
    arrows.length = w;
  }

  /** Ticks each mob has spent without the player within 32 blocks (vanilla noActionTime). */
  private readonly farTicks = new WeakMap<Mob, number>();

  private shouldDespawn(m: Mob, d2: number): boolean {
    const far = d2 > HOSTILE_SOFT_DESPAWN * HOSTILE_SOFT_DESPAWN ? (this.farTicks.get(m) ?? 0) + 1 : 0;
    this.farTicks.set(m, far);
    const persistent = (m as { persistent?: boolean }).persistent === true;
    return despawnCheck(isHostileType(m.type) || AMBIENT_TYPES.has(m.type), persistent, d2, far, this.host.rnd);
  }

  private readonly breedHooks = {
    spawn: (t: MobType, x: number, y: number, z: number) => this.spawnMob(t, x, y, z),
    dropXp: (a: number, x: number, y: number, z: number) => this.host.dropXp(a, x, y, z),
    rnd: () => this.host.rnd(),
  };
}

